import { useState, useMemo } from "react"
import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router"
import { useTranslation } from "react-i18next"
import {
  Button,
  SunIcon,
  MoonIcon,
  Badge,
  badgeColors,
  BottomNavbar,
  VoteIcon,
  CubeIcon,
  ExportIcon,
} from "@cividao/components"
import { ConfirmModal } from "../../modules/ConfirmModal"
import styles from "./index.module.css"
import classNames from "classnames"
import { useLayout } from "../../../context/LayoutContext"
import { ThemeMode } from "../../../context/LayoutContext/types"
import { useLogos } from "../../../hooks/useLogos"
import { useBlockchainMonitoring } from "../../../state"
function Breadcrumb() {
  const { t } = useTranslation(["common", "votes"])
  const location = useLocation()

  const segments = location.pathname.split("/").filter(Boolean)

  const crumbs: { label: string; path?: string }[] = []

  if (segments[0] === "votings") {
    crumbs.push({ label: t("common:votes"), path: segments.length > 1 ? "/votings" : undefined })

    if (segments[1] === "new") {
      crumbs.push({ label: t("votes:actions.create") })
    } else if (segments[1]) {
      crumbs.push({ label: t("votes:view.title") })
    }
  } else if (segments[0] === "blockchain") {
    crumbs.push({ label: t("common:blockchain") })
  }

  if (crumbs.length === 0) return null

  return (
    <nav className={styles.breadcrumb}>
      {crumbs.map((crumb, i) => (
        <span key={i} className={styles.breadcrumbItem}>
          {i > 0 && <span className={styles.breadcrumbSeparator}>/</span>}
          {crumb.path ? (
            <Link to={crumb.path} className={styles.breadcrumbLink}>
              {crumb.label}
            </Link>
          ) : (
            <span className={styles.breadcrumbCurrent}>{crumb.label}</span>
          )}
        </span>
      ))}
    </nav>
  )
}

function useBlockchainBadgeColor() {
  const { monitoringStatus } = useBlockchainMonitoring()

  return useMemo(() => {
    if (!monitoringStatus) return null
    const data = monitoringStatus.serialize()

    if (data.isBalanceCritical) return badgeColors.ERROR
    if (data.isBalanceLow) return badgeColors.WARNING
    if (data.votingSafe.pendingTransactions > 0 || data.censusSafe.pendingTransactions > 0) return badgeColors.WARNING

    return badgeColors.SUCCESS
  }, [monitoringStatus])
}

export function AdminLayout() {
  const { t } = useTranslation("common")
  const navigate = useNavigate()
  const { themeMode, setThemeMode } = useLayout()
  const { logo: logoSrc } = useLogos()
  const blockchainBadgeColor = useBlockchainBadgeColor()

  const [showLogoutModal, setShowLogoutModal] = useState(false)

  const handleLogout = async () => {
    setShowLogoutModal(false)
    await window.domain.LogoutAuthUseCase.execute()
    navigate("/sign-in")
  }

  const navItems = [
    { path: "/votings", label: t("votes") },
    { path: "/blockchain", label: t("blockchain"), badgeColor: blockchainBadgeColor },
  ]

  const { pathname: currentRoute } = useLocation()

  const handleRouteChange = (url: string) => {
    navigate(url, { viewTransition: true })
  }

  const bottomItems = [
    { label: t("votes"), url: "/votings", icon: VoteIcon },
    { label: t("blockchain"), url: "/blockchain", icon: CubeIcon },
    { label: t("logout"), icon: ExportIcon, onClick: () => setShowLogoutModal(true) },
  ]

  return (
    <div className={styles.container}>
      <aside className={styles.sidebar}>
        <div className={styles.sidebarHeader}>
          <img src={logoSrc} alt="Antipartido" className={styles.logo} />
          <span className={styles.logoTitle}>AP Admin</span>
        </div>

        <nav className={styles.nav}>
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === "/"}
              className={({ isActive }) => classNames(styles.navLink, { [styles.navLinkActive]: isActive })}
            >
              {item.label}
              {item.badgeColor && item.badgeColor !== badgeColors.SUCCESS && <Badge color={item.badgeColor} />}
            </NavLink>
          ))}
        </nav>

        <div className={styles.sidebarFooter}>
          <Button type="outline" onClick={() => setShowLogoutModal(true)} isFull>
            {t("logout")}
          </Button>
        </div>
      </aside>

      <div className={styles.mobileTopbar}>
        <div className={styles.mobileTopbarLeft}>
          <img src={logoSrc} alt="Antipartido" className={styles.logo} />
          <span className={styles.logoTitle}>AP Admin</span>
        </div>
        <Button
          type="text"
          size="small"
          onClick={() => setThemeMode(themeMode === ThemeMode.LIGHT ? ThemeMode.DARK : ThemeMode.LIGHT)}
          iconLeft={themeMode === ThemeMode.LIGHT ? <SunIcon /> : <MoonIcon />}
        />
      </div>

      <main className={styles.content}>
        <div className={styles.topbar}>
          <Breadcrumb />
          <span className={styles.desktopOnly}>
            <Button
              type="text"
              size="small"
              onClick={() => setThemeMode(themeMode === ThemeMode.LIGHT ? ThemeMode.DARK : ThemeMode.LIGHT)}
              iconLeft={themeMode === ThemeMode.LIGHT ? <SunIcon /> : <MoonIcon />}
            />
          </span>
        </div>
        <div className={styles.contentInner}>
          <Outlet />
        </div>
      </main>

      <div className={styles.mobileBottomNav}>
        <BottomNavbar
          menuItems={[]}
          bottomItems={bottomItems}
          currentRoute={currentRoute}
          showLabels
          onRouteChange={handleRouteChange}
        />
      </div>

      {showLogoutModal && (
        <ConfirmModal
          title={t("logoutConfirmTitle")}
          message={t("logoutConfirmMessage")}
          confirmColor="error"
          onConfirm={handleLogout}
          onClose={() => setShowLogoutModal(false)}
        />
      )}
    </div>
  )
}
